import { useNowTime } from "lib/hooks/useNowTime";
import { useState, useMemo, useEffect, useRef } from "react";
import { getAdminUsersDashboard } from "lib/api/getAdminUsersDashboard";
import { createAdminUser } from "lib/api/createAdminUser";
import { getAdminCountries } from "lib/api/getAdminCountries";
import { updateAdminUser } from "lib/api/updateAdminUser";
import { resetAdminUserPassword } from "lib/api/resetAdminUserPassword";
import {
  approveManagerCreationRequest,
  getManagerCreationRequests,
  rejectManagerCreationRequest,
} from "lib/api/roleAdmin";
import { useAppStore } from "lib/store/useAppStore";
import { formatSystemRole } from "lib/utils/roleLabels";
import AdminKpiGrid from "components/admin/AdminKpiGrid";
import AdminManagerRequestCard from "components/admin/AdminManagerRequestCard";
import AdminUsersTableCard from "components/admin/users/AdminUsersTableCard";
import AdminUserDetailCard from "components/admin/users/AdminUsersDetailCard";
import AdminUserCreateModal from "components/admin/users/AdminUserCreateModal";

const REQUEST_STATUS_LABELS = {
  pending: "승인 대기",
  approved: "승인 완료",
  rejected: "거절 완료",
};

function formatDate(value) {
  if (!value) return "-";
  return String(value).replace("T", " ").slice(0, 16);
}

function getRequestPayload(request) {
  return request?.payload && typeof request.payload === "object"
    ? request.payload
    : {};
}

function EmptyTableRow({ colSpan, children }) {
  return (
    <tr>
      <td className="roleAdminTable__empty" colSpan={colSpan}>
        {children}
      </td>
    </tr>
  );
}

export default function AdminUsersPage() {
  const time = useNowTime();
  const showAlert = useAppStore((s) => s.showAlert);
  const actor = useAppStore((s) => s.user);
  const [resettingPasswordId, setResettingPasswordId] = useState(null);
  const passwordResetPending = useRef(false);

  const [users, setUsers] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [activities, setActivities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [managerRequestRecords, setManagerRequestRecords] = useState([]);
  const [managerRequestError, setManagerRequestError] = useState("");

  const [selectedId, setSelectedId] = useState(null);

  const [openCreate, setOpenCreate] = useState(false);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all"); // all | master | admin | manager | user
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const [dashboardResult, countriesResult, managerRequestResult] =
        await Promise.all([
          getAdminUsersDashboard(),
          getAdminCountries(),
          getManagerCreationRequests({ status: "all" }),
        ]);

      if (cancelled) return;

      if (countriesResult.success) {
        setCountries(countriesResult.countries);
      } else {
        setCountries([]);
      }

      if (managerRequestResult.success) {
        setManagerRequestRecords(managerRequestResult.data?.requests ?? []);
        setManagerRequestError("");
      } else {
        setManagerRequestRecords([]);
        setManagerRequestError(
          managerRequestResult.message ||
            "매니저 생성 요청을 불러오지 못했습니다.",
        );
      }

      const { kpis, message, success, users, activities } = dashboardResult;

      if (!success) {
        console.warn("[getAdminUsersDashboard 실패]", message);
        setUsers([]);
        setKpis([]);
        setActivities([]);
        return;
      }

      setUsers(users);
      setKpis(kpis);
      setActivities(activities);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedUser = useMemo(
    () => users.find((u) => u.id === selectedId) ?? null,
    [users, selectedId],
  );

  const pendingManagerRequests = useMemo(
    () =>
      managerRequestRecords.filter(
        (request) => request?.status === "pending",
      ),
    [managerRequestRecords],
  );

  const recentManagerRequestRecords = useMemo(
    () => managerRequestRecords.slice(0, 12),
    [managerRequestRecords],
  );

  const filteredUsers = useMemo(() => {
    const keyword = q.trim().toLowerCase();

    return users
      .filter((u) => {
        const hit =
          !keyword ||
          (u.user_name ?? "").toLowerCase().includes(keyword) ||
          (u.email ?? "").toLowerCase().includes(keyword) ||
          (u.country_name ?? "").toLowerCase().includes(keyword) ||
          (u.country_code ?? "").toLowerCase().includes(keyword);

        const roleOk = roleFilter === "all" || u.role === roleFilter;
        const statusOk = statusFilter === "all" || u.status === statusFilter;

        return hit && roleOk && statusOk;
      })
      .sort((a, b) => (b.lastLoginTs ?? 0) - (a.lastLoginTs ?? 0));
  }, [users, q, roleFilter, statusFilter]);

  const changeRole = (id, nextRole) => {
    setUsers((prev) =>
      prev.map((u) => (u.id === id ? { ...u, role: nextRole } : u)),
    );
  };

  const toggleStatus = (id) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === id
          ? { ...u, status: u.status === "active" ? "inactive" : "active" }
          : u,
      ),
    );
  };

  const handleResetPassword = async (target) => {
    if (actor?.role !== "master" || !target?.id || passwordResetPending.current) {
      return;
    }
    const targetLabel = `${target.user_name || "사용자"} (${target.email || target.id})`;
    if (
      !window.confirm(
        `${targetLabel}님의 비밀번호를 123으로 초기화하시겠습니까?\n기존 로그인 세션이 종료됩니다. 초기화 후 비밀번호를 변경하도록 안내해 주세요.`,
      )
    ) {
      return;
    }

    passwordResetPending.current = true;
    setResettingPasswordId(target.id);
    try {
      const result = await resetAdminUserPassword(target.id);
      const isSelf =
        String(actor.id ?? actor.user_id).toLowerCase() ===
        String(target.id).toLowerCase();
      showAlert({
        text: result.success
          ? `${targetLabel}님의 비밀번호를 123으로 초기화했습니다. ${isSelf ? "다시 로그인해 주세요." : "다시 로그인한 뒤 비밀번호를 변경하도록 안내해 주세요."}`
          : result.message || "비밀번호 초기화에 실패했습니다.",
        success: result.success,
        autoClose: false,
        afterClose:
          result.success && isSelf
            ? () => useAppStore.getState().setUnauthenticated?.()
            : undefined,
      });
    } finally {
      passwordResetPending.current = false;
      setResettingPasswordId(null);
    }
  };

  const handleCreate = async (payload) => {
    // payload : { email, user_name, password, role, country_code }
    const { success, message, messageKey, messageParams } =
      await createAdminUser(payload);
    if (!success) {
      showAlert({
        text: message || "사용자 생성에 실패했습니다.",
        messageKey,
        messageParams,
        success: false,
        autoClose: true,
      });
      return;
    }

    setOpenCreate(false);

    const {
      kpis: newKpis,
      success: s2,
      users: newUsers,
      activities: newActivities,
    } = await getAdminUsersDashboard();
    if (!s2) {
      return;
    }

    setUsers(newUsers);
    setKpis(newKpis);
    setActivities(newActivities);
    showAlert({
      text: message || "사용자를 생성했습니다.",
      messageKey,
      messageParams,
      success: true,
      autoClose: true,
    });
  };

  const handleSaveChanges = async (id, payload) => {
    // payload : { role, permission }
    const { success, message, messageKey, messageParams } =
      await updateAdminUser(id, payload);
    if (!success) {
      showAlert({
        text: message || "변경사항 저장에 실패했습니다.",
        messageKey,
        messageParams,
        success: false,
        autoClose: true,
      });
      return;
    }

    const {
      kpis: newKpis,
      success: s2,
      users: newUsers,
    } = await getAdminUsersDashboard();
    if (!s2) {
      return;
    }

    setUsers(newUsers);
    setKpis(newKpis);
    showAlert({
      text: message || "변경사항을 저장했습니다.",
      messageKey,
      messageParams,
      success: true,
      autoClose: true,
    });
  };

  const refreshManagerRequests = async () => {
    const result = await getManagerCreationRequests({ status: "all" });
    if (result.success) {
      setManagerRequestRecords(result.data?.requests ?? []);
      setManagerRequestError("");
      return;
    }

    setManagerRequestRecords([]);
    setManagerRequestError(
      result.message || "매니저 생성 요청을 불러오지 못했습니다.",
    );
  };

  const refreshUsersDashboard = async () => {
    const {
      kpis: newKpis,
      success: dashboardSuccess,
      users: newUsers,
      activities: newActivities,
    } = await getAdminUsersDashboard();

    if (dashboardSuccess) {
      setUsers(newUsers);
      setKpis(newKpis);
      setActivities(newActivities);
    }
  };

  const handleReviewManagerRequest = async (id, action) => {
    const result =
      action === "approve"
        ? await approveManagerCreationRequest(id)
        : await rejectManagerCreationRequest(id);

    showAlert({
      text: result.message || "매니저 생성 요청을 처리했습니다.",
      success: result.success,
      autoClose: true,
    });

    if (result.success) {
      await refreshManagerRequests();
      await refreshUsersDashboard();
    }
  };

  return (
    <section className="content admin roleAdminPage users adminUsersPage">
      <div className="roleAdminHeader">
        <div>
          <h2>구성원 관리</h2>
          <p>{time}</p>
        </div>
        <button
          onClick={() => setOpenCreate(true)}
          className="roleAdminPrimaryButton"
          type="button"
        >
          사용자 생성
        </button>
      </div>

      <AdminKpiGrid kpis={kpis} />

      {pendingManagerRequests.length > 0 ? (
        <AdminManagerRequestCard
          managerRequests={pendingManagerRequests}
          managerRequestError={managerRequestError}
          onReview={handleReviewManagerRequest}
        />
      ) : null}

      <div className="mainGrid">
        <AdminUsersTableCard
          users={filteredUsers}
          selectedId={selectedId}
          onSelect={setSelectedId}
          selectedUser={selectedUser}
          q={q}
          setQ={setQ}
          roleFilter={roleFilter}
          setRoleFilter={setRoleFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
        />

        <AdminUserDetailCard
          selectedUser={selectedUser}
          activities={activities}
          onChangeRole={changeRole}
          onToggleStatus={toggleStatus}
          onSaveChanges={handleSaveChanges}
          onResetPassword={handleResetPassword}
          resettingPasswordId={resettingPasswordId}
          onDelete={() =>
            showAlert({
              text: "사용자 삭제 기능은 아직 사용할 수 없습니다.",
              success: false,
              autoClose: true,
            })
          }
        />

        <AdminUserCreateModal
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          onCreate={handleCreate}
          countries={countries}
        />
      </div>

      <AdminUsersRequestHistoryTable records={recentManagerRequestRecords} />
    </section>
  );
}

function AdminUsersRequestHistoryTable({ records = [] }) {
  return (
    <section className="roleAdminPanel usersRequestHistoryCard">
      <div className="roleAdminPanel__header">
        <h3>최근 요청/처리 기록</h3>
        <span className="hint muted">{records.length}건</span>
      </div>
      <div className="roleAdminTableWrap">
        <table className="roleAdminTable usersRequestHistoryTable">
          <thead>
            <tr>
              <th>구분</th>
              <th>요청자</th>
              <th>대상 계정</th>
              <th>권한</th>
              <th>상태</th>
              <th>기록일</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record) => {
              const payload = getRequestPayload(record);
              const status = record.status ?? "pending";

              return (
                <tr key={record.id}>
                  <td>매니저 생성 요청</td>
                  <td>
                    {payload.requested_by_name ||
                      payload.requested_by_email ||
                      "-"}
                  </td>
                  <td>
                    {payload.user_name || "-"} /{" "}
                    {payload.email || record.requester_email || "-"}
                  </td>
                  <td>{formatSystemRole(payload.role || "manager")}</td>
                  <td>
                    <span
                      className={`roleAdminStatus roleAdminStatus--${status}`}
                    >
                      {REQUEST_STATUS_LABELS[status] ?? status}
                    </span>
                  </td>
                  <td>{formatDate(record.reviewed_at || record.created_at)}</td>
                </tr>
              );
            })}
            {records.length === 0 ? (
              <EmptyTableRow colSpan={6}>
                최근 요청/처리 기록이 없습니다.
              </EmptyTableRow>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}
